/**
 * iter627 — Dedicated Printer Setup page.
 *
 * One-stop shop that lives outside the POS dialog so users (and their
 * staff on iPads / phones) can land here directly via /printer-setup
 * without having to open the kiosk first.
 *
 * Sections:
 *   1. Free iOS utility downloads (Epson + Star)
 *   2. Step-by-step: "I have a TM-T20III + iPad" troubleshooting
 *   3. Print Bridge download + setup
 *   4. Quick-jump to the existing POS Printer config dialog
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Printer, Download, Apple, AlertTriangle, CheckCircle2, ExternalLink, ChevronRight, Wrench, Wifi, Network, ShoppingBag, QrCode, KeyRound, Cloud, Copy, RefreshCw, FileText, Trash2, Zap, RotateCcw, Usb, Bluetooth, Server, Monitor, MapPin, PlayCircle, HelpCircle, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { renderToStaticMarkup } from 'react-dom/server';
import api from '../lib/api';
import { readPrintLog, clearPrintLog, exportPrintLogText, isCaptureXmlEnabled, setCaptureXmlEnabled, isRasterModeEnabled, setRasterModeEnabled, getPrintScale, setPrintScale } from '../lib/printLogger';
import { resetEposPrinter, printDiagnosticSlip, printImageTestSlip, clearPrintDebounceCache } from '../lib/eposPrint';

// iter628 — Render a QR code as a static SVG string so we can embed it
// directly into the print iframe (which doesn't have access to React's
// runtime). Centralised here so the poster generator and the on-page
// preview stay in sync.
function _renderQrSvgString(url) {
  return renderToStaticMarkup(
    <QRCodeSVG value={url} size={200} level="M" includeMargin={true} />
  );
}

const IOS_UTILITIES = [
  {
    id: 'epson-tm-utility',
    name: 'Epson TM Utility',
    vendor: 'Epson',
    free: true,
    purpose: 'Discover Epson thermal printers on the network. Configure IP, Wi-Fi, paper width. Send test prints.',
    works_for: ['TM-T20III', 'TM-T20IIIL', 'TM-m30II', 'TM-T88VI', 'TM-T82III'],
    url: 'https://apps.apple.com/app/epson-tm-utility/id663047879',
    badge_text: 'Recommended for all Epson printers',
  },
  {
    id: 'epson-tm-print-assistant',
    name: 'Epson TM Print Assistant',
    vendor: 'Epson',
    free: true,
    purpose: 'One-tap test prints for Epson thermal printers. Confirms LAN visibility before opening Smarto POS.',
    works_for: ['TM-T20III', 'TM-m30II', 'TM-T88VI'],
    url: 'https://apps.apple.com/app/epson-tm-print-assistant/id1071346767',
    badge_text: 'Quick verification tool',
  },
  {
    id: 'star-quick-setup',
    name: 'Star Quick Setup Utility',
    vendor: 'Star Micronics',
    free: true,
    purpose: 'Configure Wi-Fi + AirPrint settings on Star thermal printers.',
    works_for: ['TSP100', 'TSP143', 'mC-Print3', 'mC-Print2'],
    url: 'https://apps.apple.com/app/star-quick-setup-utility/id1186403709',
    badge_text: 'For Star printers',
  },
];

const TMT20III_STEPS = [
  {
    n: 1,
    title: 'Note the printer\'s IP address',
    body: 'Power-cycle the printer while holding the FEED button. It prints a self-test page — look for "IP Address: 192.168.x.x" near the top. Or use the Epson TM Utility app to scan the LAN.',
  },
  {
    n: 2,
    title: 'Open Smarto → POS → Printer Settings',
    body: 'Click the orange Printer icon in the POS toolbar (top-right) → "Add Printer" → enter the IP from step 1. Leave port at 9100 (raw ESC/POS). Paper width 80mm for most TM-T20III rolls.',
  },
  {
    n: 3,
    title: 'Click Test Print',
    body: 'If the printer beeps and spits out a "Smarto test print" receipt, you\'re connected. If not, see "Why is it not printing from iPad?" below.',
  },
];

const NOT_PRINTING_REASONS = [
  {
    icon: AlertTriangle,
    title: 'TM-T20III does NOT support AirPrint',
    body: 'iOS\'s built-in print dialog (AirPrint) cannot see the TM-T20III no matter what network it\'s on. This is a hardware limitation, not a Smarto bug. You need one of the three fixes below.',
    severity: 'error',
  },
  {
    icon: CheckCircle2,
    title: 'Fix 1: Use the Smarto Print Bridge (Free, Recommended)',
    body: 'Download the Print Bridge for Mac / Windows / Linux. Run it on ANY computer that\'s on the same router as the printer (a cheap old laptop works fine, or the manager\'s desk PC). Smarto on the iPad sends the receipt to the Bridge over Wi-Fi, the Bridge sends ESC/POS to the printer over Ethernet. Setup takes ~5 minutes.',
    severity: 'success',
  },
  {
    icon: Network,
    title: 'Fix 2: AirPrint-enabling box (~$120)',
    body: 'Buy a Lantronix xPrintServer or similar. It plugs into your router and makes the TM-T20III visible as an AirPrint printer to all iPads on the LAN. No software changes needed in Smarto.',
    severity: 'info',
  },
  {
    icon: ShoppingBag,
    title: 'Fix 3: Upgrade to an AirPrint-native printer',
    body: 'Epson TM-m30II or TM-T88VI-iHub both ship with AirPrint built in. Costs more upfront but plug-and-play — iPad sees it automatically, no Bridge needed. Best for kiosk-style setups where staff change frequently.',
    severity: 'info',
  },
];

const CONN_TYPES = [
  { id: 'usb', label: 'USB', icon: <Usb size={12} /> },
  { id: 'bluetooth', label: 'Bluetooth', icon: <Bluetooth size={12} /> },
  { id: 'network', label: 'Network/IP', icon: <Network size={12} /> },
  { id: 'print_bridge', label: 'Print Bridge', icon: <Server size={12} /> },
];
const CONN_HELP = {
  usb: 'Plug the printer into the terminal with a USB cable. On Android and Windows it is detected automatically — add it in step 2 and send a test slip. (USB printing is not available on iPad.)',
  bluetooth: 'Pair the printer in your device\u2019s Bluetooth settings first, then add it in step 2. Best for mobile Android terminals.',
  network: 'Put the printer on the same Wi-Fi/LAN as this terminal, note its IP address, then add it in step 2 using port 9100 (raw ESC/POS). Paper width 80mm for most thermal rolls.',
  print_bridge: 'For iPads (which cannot talk to ESC/POS printers directly): run the free Smarto Print Bridge on any Mac/PC on the same router. The iPad sends the receipt to the Bridge, which forwards it to the printer.',
};

export default function PrinterSetupPage() {
  const nav = useNavigate();
  const [openStep, setOpenStep] = useState(null);

  // iter9xx — Guided setup + platform gating. The page historically dumped
  // every iPad / Print-Bridge warning on all devices; now the loud iPad
  // sections only show on Apple platforms (or when the user opts in) and the
  // diagnostic tools live behind an Advanced toggle. The printing ENGINE
  // (eposPrint / bridge / relay / diagnostics) is reused unchanged.
  const deviceId = (() => { try { return localStorage.getItem('smarto_device_id') || ''; } catch { return ''; } })();
  const terminalBranchId = (() => { try { return localStorage.getItem('pos_terminal_branch_id') || ''; } catch { return ''; } })();
  const isApple = typeof navigator !== 'undefined' && (/iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1));
  const [showAppleHelp, setShowAppleHelp] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const appleHelp = isApple || showAppleHelp;
  const [printers, setPrinters] = useState([]);
  const [connType, setConnType] = useState('network');
  const [stepperTesting, setStepperTesting] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/cashier/printers'); setPrinters(data.printers || data || []); }
      catch { /* endpoint optional */ }
    })();
  }, []);
  const stepperPrinter = () => {
    const list = printers.filter(p => !terminalBranchId || p.branch_id === terminalBranchId || !p.branch_id);
    return list.find(p => p.is_default) || list[0] || null;
  };
  const runStepperTest = async () => {
    const p = stepperPrinter();
    if (!p) { toast.error('No printer configured yet — add one in step 2 first.'); return; }
    setStepperTesting(true);
    const tId = toast.loading('Sending a test slip…');
    try {
      const r = await printDiagnosticSlip(p);
      if (r.ok) toast.success('Test slip sent. If a small SMARTO receipt prints, you are all set.', { id: tId });
      else toast.error(r.msg || r.error || 'Test print failed — see Advanced › Print Log.', { id: tId });
    } catch (e) {
      toast.error('Test print failed — see Advanced › Print Log.', { id: tId });
    } finally { setStepperTesting(false); }
  };

  // iter646 — Cloud Relay token. The Bridge running on a Mac/PC at the
  // restaurant pastes this token to register with Smarto cloud, then
  // iPads can print without ever reaching localhost:9101.
  const [relayToken, setRelayToken] = useState('');
  const [relayLoading, setRelayLoading] = useState(false);
  const [bridges, setBridges] = useState([]);
  const fetchRelay = async () => {
    setRelayLoading(true);
    try {
      const [{ data: t }, { data: b }] = await Promise.all([
        api.get('/cashier/print-bridge/relay-token'),
        api.get('/cashier/print-bridge/bridges'),
      ]);
      setRelayToken(t.relay_token || '');
      setBridges(b.bridges || []);
    } catch (err) {
      // Endpoint may not exist on older deploys — fail silently.
    } finally {
      setRelayLoading(false);
    }
  };
  useEffect(() => { fetchRelay(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const rotateRelay = async () => {
    if (!window.confirm('Rotate the relay token? Any Bridge using the old token will stop receiving jobs until restarted with the new token.')) return;
    try {
      const { data } = await api.post('/cashier/print-bridge/relay-token/rotate');
      setRelayToken(data.relay_token || '');
      toast.success('Relay token rotated — paste it on every Bridge machine');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rotate failed');
    }
  };
  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(relayToken);
      toast.success('Relay token copied');
    } catch {
      toast.error('Could not copy — select manually');
    }
  };

  // iter645 — Bridge diagnostic. One-click probe of /health, /version,
  // and /ping on http://localhost:9101. Surfaces the exact reason the
  // banner is yellow so users don't have to guess.
  const [diag, setDiag] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);
  const runDiagnostic = async () => {
    setDiagRunning(true);
    const out = { ts: new Date().toLocaleTimeString(), checks: [] };
    const probes = [
      { name: 'GET /health (Bridge alive?)', url: 'http://localhost:9101/health' },
      { name: 'GET /version (Bridge version)', url: 'http://localhost:9101/version' },
      { name: 'POST /ping (CORS allowed?)', url: 'http://localhost:9101/ping', init: { method: 'POST' } },
    ];
    for (const p of probes) {
      const t0 = performance.now();
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 3000);
        const res = await fetch(p.url, { ...(p.init || {}), signal: ctl.signal });
        clearTimeout(t);
        let body = '';
        try { body = await res.text(); } catch { /* binary or empty */ }
        out.checks.push({
          name: p.name,
          ok: res.ok,
          status: res.status,
          ms: Math.round(performance.now() - t0),
          body: (body || '').slice(0, 200),
        });
      } catch (e) {
        out.checks.push({
          name: p.name,
          ok: false,
          status: 0,
          ms: Math.round(performance.now() - t0),
          error: String(e?.message || e),
        });
      }
    }
    out.overall = out.checks.every(c => c.ok);
    setDiag(out);
    setDiagRunning(false);
  };

  // iter722 — Print Log viewer. Every ePOS print attempt logs to
  // localStorage via printLogger.js. This block surfaces the log so
  // the user can copy / download / share it for diagnosis.
  const [logRows, setLogRows] = useState([]);
  const [logExpanded, setLogExpanded] = useState(false);
  // iter741 — Developer toggle: capture raw ePOS XML payload into each
  // print log row. OFF by default so localStorage stays small for
  // normal cashiers; ON only when an operator is hunting a "log
  // shows OK but no paper" bug.
  const [captureXml, setCaptureXml] = useState(() => isCaptureXmlEnabled());
  const toggleCaptureXml = () => {
    const next = !captureXml;
    setCaptureXmlEnabled(next);
    setCaptureXml(next);
    toast.success(next
      ? 'Capture XML to Print Log is ON. Print a receipt, then expand the log row to inspect the raw payload.'
      : 'Capture XML to Print Log is OFF. New rows will no longer store the raw payload.');
  };
  const [xmlExpandedRow, setXmlExpandedRow] = useState(null);

  const [rasterMode, setRasterMode] = useState(() => isRasterModeEnabled());
  const toggleRasterMode = () => {
    const next = !rasterMode;
    setRasterModeEnabled(next);
    setRasterMode(next);
    toast.success(next
      ? 'Print receipt as IMAGE is ON. Arabic + logo now print as a bitmap — works on any thermal printer.'
      : 'Print receipt as IMAGE is OFF. Receipts print as plain text (faster, but Arabic depends on the printer font).');
  };
  const [printScale, setPrintScaleState] = useState(() => getPrintScale());
  const changePrintScale = (scale) => {
    setPrintScale(scale);
    setPrintScaleState(scale);
    toast.success(scale === 'large'
      ? 'Print size set to LARGE — bigger, easier-to-read receipts.'
      : 'Print size set to NORMAL.');
  };
  const copyXmlRow = async (xml) => {
    try {
      await navigator.clipboard.writeText(xml);
      toast.success('Raw ePOS XML copied to clipboard.');
    } catch {
      toast.error('Clipboard blocked — long-press the box and copy manually.');
    }
  };
  const refreshLog = () => setLogRows(readPrintLog());
  useEffect(() => { refreshLog(); }, []);
  const copyLog = async () => {
    const txt = exportPrintLogText();
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`Copied ${logRows.length} log row(s). Paste into your support message.`);
    } catch {
      toast.error('Could not copy — long-press the box below and copy manually.');
    }
  };
  const downloadLog = () => {
    const txt = exportPrintLogText();
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smarto-print-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success('Log downloaded — share the .txt file with support.');
  };
  const wipeLog = () => {
    if (!confirm(`Clear all ${logRows.length} print log row(s)? This cannot be undone.`)) return;
    clearPrintLog();
    refreshLog();
    toast.success('Print log cleared.');
  };

  // iter732 — Printer Memory Reset block. When the printer's network
  // ACKs every print as "OK" but no paper actually comes out, the
  // hardware is usually stuck in an ASB error state (recoverable
  // firmware halt — cover-open false-positive, paper-near-end flag
  // not cleared, buffer overflow from a prior bad XML, etc). The
  // Epson ePOS `<recovery/>` element clears that state without a
  // power-cycle. We also flush our in-app debounce cache so the
  // next print isn't suppressed as a duplicate.
  const [resetIp, setResetIp] = useState('');
  const [resetDevId, setResetDevId] = useState('local_printer');
  const [resetPaper, setResetPaper] = useState('80mm');
  const [resetBusy, setResetBusy] = useState(false);

  // Pre-fill from the most recent print log row so the cashier doesn't
  // have to re-type the IP / devid every time they need to reset.
  useEffect(() => {
    if (resetIp || logRows.length === 0) return;
    const latest = [...logRows].reverse().find(r => r.printer?.ip);
    if (latest?.printer?.ip) setResetIp(latest.printer.ip);
    if (latest?.printer?.deviceId) setResetDevId(latest.printer.deviceId);
    if (latest?.printer?.paper_width) setResetPaper(latest.printer.paper_width);
  }, [logRows, resetIp]);

  const _makeResetPrinter = () => ({
    name: `Reset target (${resetIp.trim()})`,
    ip_address: resetIp.trim(),
    epos_device_id: resetDevId.trim() || 'local_printer',
    paper_width: resetPaper,
  });

  // Full memory-reset flow (per user spec):
  //   1. Send <recovery/> to flush the printer's hardware buffer.
  //   2. Clear the in-app `_RECENT_PRINTS` debounce cache.
  //   3. Send a tiny diagnostic slip so the cashier can SEE the
  //      printer responded.
  const runFullReset = async () => {
    if (!resetIp.trim()) {
      toast.error('Enter the printer IP first.');
      return;
    }
    setResetBusy(true);
    const printer = _makeResetPrinter();
    const tId = toast.loading(`Resetting ${resetIp.trim()}…`);
    try {
      const r1 = await resetEposPrinter(printer); // also clears debounce
      // Tiny gap so the printer fully exits recovery before next job.
      await new Promise(res => setTimeout(res, 400));
      const r2 = await printDiagnosticSlip(printer);
      refreshLog();
      if (r1.ok && r2.ok) {
        toast.success(`Reset + diagnostic sent to ${resetIp.trim()} (devid="${resetDevId.trim() || 'local_printer'}"). If a tiny SMARTO slip prints, the hardware is healthy.`, { id: tId });
      } else if (r1.ok && !r2.ok) {
        toast.error(`Reset OK but diagnostic failed: ${r2.msg || r2.error}`, { id: tId });
      } else {
        toast.error(r1.msg || `Reset failed: ${r1.error}`, { id: tId });
      }
    } finally {
      setResetBusy(false);
    }
  };

  const runDiagnosticOnly = async () => {
    if (!resetIp.trim()) {
      toast.error('Enter the printer IP first.');
      return;
    }
    setResetBusy(true);
    const tId = toast.loading(`Sending diagnostic slip to ${resetIp.trim()}…`);
    try {
      const r = await printDiagnosticSlip(_makeResetPrinter());
      refreshLog();
      if (r.ok) toast.success(`Diagnostic sent. Check the paper for a tiny "SMARTO" slip.`, { id: tId });
      else toast.error(r.msg || `Diagnostic failed: ${r.error}`, { id: tId });
    } finally { setResetBusy(false); }
  };

  const flushDebounceOnly = () => {
    const r = clearPrintDebounceCache();
    toast.success(`Cleared ${r.cleared} debounced print${r.cleared === 1 ? '' : 's'} from in-app cache. Next print will fire fresh.`);
  };

  const runImageTest = async () => {
    if (!resetIp.trim()) {
      toast.error('Enter the printer IP first.');
      return;
    }
    setResetBusy(true);
    const tId = toast.loading(`Sending IMAGE test to ${resetIp.trim()}…`);
    try {
      const r = await printImageTestSlip(_makeResetPrinter());
      refreshLog();
      if (r.ok) toast.success(`Image test sent. If an "IMAGE OK" box prints → raster works. If text printed but the box did NOT → the printer can't render images.`, { id: tId, duration: 9000 });
      else toast.error(r.msg || `Image test failed: ${r.error}`, { id: tId });
    } finally { setResetBusy(false); }
  };

  // iter630 — One-tap LAN scan via the Print Bridge's /discover endpoint.
  // The Bridge auto-scans on boot too, but users can re-trigger from here.
  const [discovered, setDiscovered] = useState({ status: 'idle', printers: [], bridge_url: 'http://localhost:9101' });
  const triggerDiscovery = async () => {
    setDiscovered(d => ({ ...d, status: 'scanning' }));
    try {
      const res = await fetch(`${discovered.bridge_url}/discover`, { method: 'POST' });
      const body = await res.json();
      setDiscovered(d => ({ ...d, status: 'done', printers: body.printers || [] }));
    } catch (e) {
      setDiscovered(d => ({ ...d, status: 'error', printers: [] }));
    }
  };

  // iter632 — One-click test print via the local Bridge.
  // Browser → http://localhost:9101/print → printer. Skips Smarto cloud
  // (the cloud can't reach a LAN printer anyway) but covers the same
  // end-to-end path a real receipt takes.
  const [testIp, setTestIp] = useState('');
  const [testing, setTesting] = useState(false);
  const pushTestPrint = async () => {
    if (!testIp.trim()) { return alert('Enter the printer IP first'); }
    setTesting(true);
    try {
      const res = await fetch(`${discovered.bridge_url}/print`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ip: testIp.trim(), port: 9100,
          title: 'TEST PRINT',
          company: 'Smarto',
          header: 'Push test from Smarto cloud',
          items: [{ name: 'Test item', qty: 1, subtotal: 0 }],
          subtotal: 0, total: 0, currency: 'SAR',
          footer: 'Your printer is wired up — go go go!',
        }),
      });
      const body = await res.json();
      if (res.ok) {
        alert(`✅ Sent ${body.bytes || 0} bytes to ${testIp}:9100`);
      } else {
        alert(`❌ ${body.error || 'Print failed'}`);
      }
    } catch (e) {
      alert(`❌ Could not reach the Bridge at ${discovered.bridge_url}. Is it running?`);
    } finally { setTesting(false); }
  };

  // iter628 — Build a printable A4 cheat-sheet with a QR pointing
  // back to /printer-setup, so branch managers can stick it next
  // to the till and staff scan-to-self-serve.
  const printAsPoster = () => {
    const url = `${window.location.origin}/printer-setup`;
    // Build offscreen iframe with poster HTML + auto-print.
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Smarto Printer Setup — Branch Cheat Sheet</title>
      <style>
        @page { size: A4; margin: 14mm; }
        * { box-sizing: border-box; font-family: -apple-system, system-ui, sans-serif; }
        body { color:#1f2937; }
        h1 { font-size: 32px; margin: 0 0 4px 0; color:#4f46e5; }
        .lead { font-size: 14px; color:#6b7280; margin-bottom: 22px; }
        .row { display:flex; gap: 28px; align-items: flex-start; margin-bottom: 24px; }
        .qrcell { flex: 0 0 230px; text-align:center; }
        .qrcell svg { border: 4px solid #e0e7ff; padding: 10px; background:#fff; border-radius: 14px; }
        .qrcaption { font-size: 11px; color: #6366f1; margin-top: 8px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }
        .stepcell { flex: 1; }
        ol { margin: 0; padding-left: 20px; }
        ol li { margin-bottom: 10px; font-size: 14px; line-height: 1.5; }
        ol li b { color: #1f2937; }
        .warn { background: #fef3c7; border: 1px solid #fbbf24; padding: 12px 14px; border-radius: 10px; font-size: 12px; line-height: 1.5; margin-top: 14px; }
        .warn b { color: #92400e; }
        .fixes { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-top: 14px; }
        .fixes .fix { background:#ecfdf5; border:1px solid #6ee7b7; border-radius:10px; padding: 10px 12px; font-size: 11px; line-height: 1.4; }
        .fixes .fix b { color: #065f46; display:block; margin-bottom: 4px; }
        .footer { margin-top: 24px; font-size: 10px; color: #9ca3af; text-align:center; border-top: 1px dashed #e5e7eb; padding-top: 12px; }
        .url { font-family: monospace; background:#f3f4f6; padding:2px 6px; border-radius:4px; font-size: 11px; }
      </style></head>
      <body>
        <h1>📠 Need to print receipts from iPad?</h1>
        <div class="lead">A 60-second self-service guide for staff at this branch.</div>
        <div class="row">
          <div class="qrcell" id="qrslot"></div>
          <div class="stepcell">
            <ol>
              <li><b>Scan the QR code</b> with your iPad / iPhone camera. It opens the full Smarto Printer Setup guide in Safari.</li>
              <li><b>Install the free Epson TM Utility</b> from the App Store (the QR page has the direct link). This lets your iPad <i>configure</i> the printer.</li>
              <li><b>Find the printer's IP</b> — power-cycle the printer while holding the FEED button; it prints a self-test page with the IP.</li>
              <li><b>Open Smarto POS → Printer Settings → Add Printer</b>, paste the IP, port <span class="url">9100</span>, paper <span class="url">80mm</span>, hit <b>Save + Test Print</b>.</li>
            </ol>
            <div class="warn">
              <b>⚠️ Got &quot;not allowed to print&quot;?</b><br/>
              That's the TM-T20III hardware limit — it doesn't support AirPrint natively. Use one of the three fixes below:
            </div>
            <div class="fixes">
              <div class="fix"><b>1. Smarto Print Bridge (Free)</b>Run on any Mac/PC on the same router — iPad sends print → Bridge → printer.</div>
              <div class="fix"><b>2. AirPrint box (~$120)</b>Lantronix xPrintServer makes any Ethernet printer visible to iPad.</div>
              <div class="fix"><b>3. Upgrade printer</b>Epson TM-m30II or TM-T88VI-iHub ship with AirPrint built-in.</div>
            </div>
          </div>
        </div>
        <div class="footer">Generated from <span class="url">${url}</span> · Stick this near the till · Powered by Smarto ERP</div>
        <script>
          // Inject QR — we serialise the QRCodeSVG component to a string
          // from the parent before opening so we can embed it directly.
          document.getElementById('qrslot').innerHTML = ${JSON.stringify(_renderQrSvgString(url))} + '<div class="qrcaption">Scan with iPad camera</div>';
          window.onload = () => setTimeout(() => window.print(), 250); // eslint-print-native-ok — printer setup preview
        </script>
      </body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
    setTimeout(() => { try { iframe.remove(); } catch { /* */ } }, 60000);
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="printer-setup-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit flex items-center gap-2">
              <Printer className="text-indigo-600" size={28} /> Printer Setup
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Get your thermal printer talking to Smarto from iPad, iPhone, Android, Mac, or Windows.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={printAsPoster} data-testid="print-poster-btn"
                    className="bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100">
              <QrCode size={14} className="mr-1" /> Print as poster (QR cheat sheet)
            </Button>
            <Button variant="outline" onClick={() => nav('/cashier/pos')} data-testid="open-pos-printers">
              <ChevronRight size={14} className="mr-1" /> Configure printer IP in POS
            </Button>
          </div>
        </div>

        {/* iter9xx — GUIDED SETUP: Terminal → Printer → Connection → Configure → Test */}
        <Card className="border-indigo-200 shadow-sm" data-testid="guided-setup-card">
          <CardContent className="p-4 sm:p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Wrench size={18} className="text-indigo-600" />
              <h2 className="font-semibold font-outfit text-base">Guided printer setup</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="guided-steps">
              <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-3 bg-white/70 dark:bg-stone-900/40" data-testid="step-terminal">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700"><Monitor size={14} /> 1 · Terminal</div>
                <div className="mt-2 text-[11px] text-muted-foreground">This device</div>
                <div className="font-mono text-[10px] break-all mt-0.5">{deviceId || 'not registered'}</div>
                <div className="text-[11px] mt-1 flex items-center gap-1"><MapPin size={11} /> {terminalBranchId ? 'Branch set' : 'No branch set'}</div>
              </div>
              <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-3 bg-white/70 dark:bg-stone-900/40" data-testid="step-printer">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700"><Printer size={14} /> 2 · Printer</div>
                <div className="mt-2 text-[11px] text-muted-foreground">{printers.length} configured</div>
                <Button size="sm" variant="outline" className="h-7 text-[11px] mt-2 w-full" onClick={() => nav('/settings/printers')} data-testid="step-printer-manage">Add / manage</Button>
              </div>
              <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-3 bg-white/70 dark:bg-stone-900/40" data-testid="step-connection">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700"><Network size={14} /> 3 · Connection</div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  {CONN_TYPES.map(c => (
                    <button key={c.id} onClick={() => setConnType(c.id)}
                      className={`text-[10px] rounded-md border px-1.5 py-1 flex items-center justify-center gap-1 transition-colors ${connType === c.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-stone-900 border-stone-200 text-stone-600 hover:border-indigo-300'}`}
                      data-testid={`conn-${c.id}`}>{c.icon} {c.label}</button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-3 bg-white/70 dark:bg-stone-900/40" data-testid="step-configure">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700"><SlidersHorizontal size={14} /> 4 · Configure</div>
                <div className="mt-2 text-[11px] text-muted-foreground">Set IP / paper width</div>
                <Button size="sm" variant="outline" className="h-7 text-[11px] mt-2 w-full" onClick={() => nav('/cashier/pos')} data-testid="step-configure-btn">Open in POS</Button>
              </div>
              <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-3 bg-white/70 dark:bg-stone-900/40" data-testid="step-test">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700"><PlayCircle size={14} /> 5 · Test print</div>
                <div className="mt-2 text-[11px] text-muted-foreground">Print a test slip</div>
                <Button size="sm" className="h-7 text-[11px] mt-2 w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={runStepperTest} disabled={stepperTesting} data-testid="step-test-btn">{stepperTesting ? 'Sending…' : 'Test print'}</Button>
              </div>
            </div>
            <div className="rounded-lg bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30 p-3 text-[12px] leading-relaxed" data-testid="conn-instructions">
              {CONN_HELP[connType]}
              {connType === 'print_bridge' && !appleHelp && (
                <button className="ml-1 underline text-indigo-700" onClick={() => setShowAppleHelp(true)} data-testid="conn-open-apple-help">Show iPad / Print Bridge help ↓</button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Optional sections — iPad help gated to Apple platforms, tools behind Advanced */}
        <div className="flex flex-wrap gap-2" data-testid="setup-toggles">
          {!isApple && (
            <Button variant="outline" size="sm" className={showAppleHelp ? 'bg-rose-50 border-rose-300 text-rose-700' : ''} onClick={() => setShowAppleHelp(v => !v)} data-testid="toggle-apple-help">
              <Apple size={14} className="mr-1" /> {showAppleHelp ? 'Hide iPad help' : 'Printing from an iPad?'}
            </Button>
          )}
          <Button variant="outline" size="sm" className={showAdvanced ? 'bg-amber-50 border-amber-300 text-amber-800' : ''} onClick={() => setShowAdvanced(v => !v)} data-testid="toggle-advanced">
            <SlidersHorizontal size={14} className="mr-1" /> {showAdvanced ? 'Hide advanced tools' : 'Advanced tools & diagnostics'}
          </Button>
        </div>

        {appleHelp && (<>
        {/* iter629 — Crystal-clear blocker banner for iPad users. */}
        <Card className="border-2 border-rose-300 bg-gradient-to-br from-rose-50 to-orange-50" data-testid="ipad-blocker-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={28} className="text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h2 className="text-base font-bold text-rose-900">
                  Got &quot;Print bridge not running&quot; on your iPad? Read this first.
                </h2>
                <p className="text-xs text-rose-900/90 leading-relaxed">
                  <strong>The Print Bridge cannot be installed on the iPad itself.</strong> Apple iOS does not allow background HTTP servers.
                  The Bridge is a tiny Python script that runs on a <strong>Mac, Windows, or Linux computer</strong> — ANY computer on the same Wi-Fi router as your printer. (A cheap old laptop on a shelf works perfectly. The manager&apos;s desk PC works. A Mac Mini works.)
                </p>
                <div className="grid sm:grid-cols-3 gap-2 text-[11px]">
                  <div className="p-2 rounded bg-white border border-rose-200">
                    <strong className="text-rose-800">📱 iPad</strong>
                    <div className="text-stone-600 mt-1">Opens Smarto, taps Print → sends job to cloud</div>
                  </div>
                  <div className="p-2 rounded bg-white border border-emerald-300">
                    <strong className="text-emerald-800">💻 Mac/PC (Bridge here)</strong>
                    <div className="text-stone-600 mt-1">Cloud forwards → Bridge → ESC/POS to printer</div>
                  </div>
                  <div className="p-2 rounded bg-white border border-indigo-200">
                    <strong className="text-indigo-800">🖨️ TM-T20II/III</strong>
                    <div className="text-stone-600 mt-1">Connected to same router via Ethernet cable</div>
                  </div>
                </div>
                <p className="text-[11px] text-rose-700">
                  <strong>Don&apos;t have a spare PC?</strong> Skip the Bridge and either (1) buy a Lantronix xPrintServer (~$120) which makes your printer AirPrint-visible, or (2) replace TM-T20II/III with Epson TM-m30II (AirPrint built-in).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* iter628 — QR cheat-sheet preview card */}
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50/40 to-orange-50/40" data-testid="qr-poster-preview">
          <CardContent className="p-5">
            <div className="flex items-center gap-5 flex-wrap">
              <div className="shrink-0 p-3 bg-white border-4 border-amber-300 rounded-xl">
                <QRCodeSVG
                  value={typeof window !== 'undefined' ? `${window.location.origin}/printer-setup` : '/printer-setup'}
                  size={120} level="M" includeMargin={false}
                />
              </div>
              <div className="flex-1 min-w-[260px] space-y-2">
                <div className="flex items-center gap-2">
                  <QrCode size={18} className="text-amber-700" />
                  <h2 className="text-lg font-bold font-outfit">Print this as a wall poster</h2>
                  <Badge className="bg-amber-600 text-white text-[10px]">NEW</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Click the orange button above to generate an A4 cheat sheet with this QR code + the
                  3-step setup guide. Stick it next to the till. When new staff see &quot;iPad won&apos;t
                  print&quot;, they scan and self-serve — no IT call needed.
                </p>
                <div className="text-[10px] text-amber-700 font-mono break-all">
                  {typeof window !== 'undefined' ? `${window.location.origin}/printer-setup` : ''}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 1 — iOS utility downloads (always visible) */}
        <Card className="border-indigo-200" data-testid="ios-utilities-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Apple size={18} className="text-indigo-600" />
              <h2 className="text-lg font-bold font-outfit">Step 1 — Free iOS utility (configure your printer)</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              iOS doesn&apos;t accept driver downloads. Use the manufacturer&apos;s free App Store utility to discover your printer on the network and configure its IP / Wi-Fi. <strong>This is the same app Epson and Star ship to all retail customers — it&apos;s 100% free.</strong>
            </p>
            <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-3">
              {IOS_UTILITIES.map(u => (
                <a key={u.id} href={u.url} target="_blank" rel="noreferrer"
                   data-testid={`ios-util-${u.id}`}
                   className="block p-3 rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white hover:from-indigo-100/60 hover:border-indigo-400 transition group">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">{u.name}</div>
                    {u.free && <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">FREE</Badge>}
                  </div>
                  <div className="text-[11px] text-indigo-700 mt-0.5">{u.badge_text}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{u.purpose}</div>
                  <div className="text-[10px] text-stone-500 mt-2">Works with: {u.works_for.join(', ')}</div>
                  <div className="text-[11px] text-indigo-600 mt-2 group-hover:underline flex items-center gap-1">
                    Open in App Store <ExternalLink size={11} />
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* SECTION 2 — "I just installed TM Utility, how do I connect to Smarto?" */}
        <Card className="border-amber-200 bg-amber-50/30" data-testid="connect-smarto-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Wrench size={18} className="text-amber-700" />
              <h2 className="text-lg font-bold font-outfit">Step 2 — Connect your printer to Smarto</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              <strong>Just installed Epson TM Utility?</strong> Good — that&apos;s only for printer configuration. To actually print receipts from Smarto you also need to tell Smarto the printer&apos;s IP. Here&apos;s the full flow for an Epson TM-T20III:
            </p>
            <div className="space-y-2">
              {TMT20III_STEPS.map(s => (
                <div key={s.n}
                     onClick={() => setOpenStep(openStep === s.n ? null : s.n)}
                     className={`p-3 rounded-lg border bg-white cursor-pointer transition ${openStep === s.n ? 'border-amber-400 shadow-sm' : 'border-amber-200 hover:border-amber-300'}`}
                     data-testid={`step-${s.n}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{s.n}</div>
                    <div className="font-semibold text-sm flex-1">{s.title}</div>
                    <ChevronRight size={14} className={`transition-transform ${openStep === s.n ? 'rotate-90' : ''}`} />
                  </div>
                  {openStep === s.n && (
                    <div className="text-xs text-stone-700 mt-2 ml-9 leading-relaxed">{s.body}</div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs flex items-start gap-2">
              <Wifi size={14} className="text-indigo-600 mt-0.5 shrink-0" />
              <div>
                <strong>Same network check:</strong> Your printer (Ethernet) and your iPad (Wi-Fi) must be on the <em>same router</em>. If they&apos;re on different networks (e.g., guest Wi-Fi vs main router), nothing will work even with the right software. Confirm by pinging the printer IP from a laptop on the same Wi-Fi as the iPad.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 3 — Why iPad shows "not allowed to print" — TM-T20III troubleshooting */}
        <Card className="border-rose-200" data-testid="troubleshoot-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-600" />
              <h2 className="text-lg font-bold font-outfit">Step 3 — Why is my iPad saying &quot;not allowed to print&quot;?</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              If you tap Print on an iPad and see <em>&quot;this is not allowed to print&quot;</em> or no printer appears in the AirPrint dialog, here&apos;s what&apos;s happening and how to fix it:
            </p>
            <div className="space-y-2">
              {NOT_PRINTING_REASONS.map((r, i) => {
                const Icon = r.icon;
                const colors = {
                  error: 'border-rose-300 bg-rose-50/50',
                  success: 'border-emerald-300 bg-emerald-50/50',
                  info: 'border-indigo-200 bg-indigo-50/40',
                }[r.severity];
                const iconColor = { error: 'text-rose-600', success: 'text-emerald-600', info: 'text-indigo-600' }[r.severity];
                return (
                  <div key={i} className={`p-3 rounded-lg border ${colors}`} data-testid={`troubleshoot-${i}`}>
                    <div className="flex items-start gap-2">
                      <Icon size={16} className={`shrink-0 mt-0.5 ${iconColor}`} />
                      <div>
                        <div className="font-semibold text-sm">{r.title}</div>
                        <div className="text-xs text-stone-700 mt-1 leading-relaxed">{r.body}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        </>)}

        {showAdvanced && (<>
        {/* SECTION 4 — Print Bridge download (single Python file, runs on Mac/PC NOT iPad) */}
        <Card className="border-emerald-200 bg-emerald-50/30" data-testid="bridge-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Download size={18} className="text-emerald-600" />
              <h2 className="text-lg font-bold font-outfit">Step 4 — Smarto Print Bridge (recommended fix)</h2>
              <Badge className="bg-emerald-600 text-white text-[10px]">FREE</Badge>
              <Badge variant="outline" className="text-[10px] border-rose-400 text-rose-700">⚠️ Runs on Mac/PC — NOT on iPad</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              The Bridge is a tiny program that runs on ANY PC at your branch so the browser can talk to the printer. <strong>Download → double-click → done.</strong> It auto-starts on every boot — you never touch it again.
            </p>

            {/* iter655 — 1-CLICK installers (NEW prominent block). Big green
                buttons that handle Python download + script + auto-start in
                one shot. The old manual Python instructions remain below
                under a collapsed "Manual install" section as a fallback. */}
            <div className="grid sm:grid-cols-2 gap-3 mt-1">
              <a
                href="/downloads/smarto_print_bridge_installer.bat"
                download="SmartoPrintBridge_Installer.bat"
                className="group block p-4 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-600 hover:shadow-lg transition"
                data-testid="bridge-installer-windows"
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                    <Download size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-sm">Windows — 1-click installer</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Double-click. Installs Python, the Bridge, and auto-start in 30 seconds. Works on Windows 10 / 11.
                    </div>
                    <div className="text-[10px] mt-1.5 text-emerald-700 font-semibold inline-flex items-center gap-1">
                      <CheckCircle2 size={11} /> Recommended
                    </div>
                  </div>
                </div>
              </a>

              <a
                href="/downloads/smarto_print_bridge_installer.command"
                download="SmartoPrintBridge_Installer.command"
                className="group block p-4 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-600 hover:shadow-lg transition"
                data-testid="bridge-installer-mac"
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                    <Apple size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-sm">macOS — 1-click installer</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Double-click. Sets up the Bridge as a LaunchAgent so it auto-runs on every login.
                    </div>
                    <div className="text-[10px] mt-1.5 text-emerald-700 font-semibold inline-flex items-center gap-1">
                      <CheckCircle2 size={11} /> Recommended
                    </div>
                  </div>
                </div>
              </a>
            </div>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 mt-2">
              <strong>Windows note:</strong> after double-clicking the <code>.bat</code> file, Windows may show a blue &quot;Microsoft Defender SmartScreen&quot; warning (this is normal for any unsigned installer). Click <strong>&quot;More info&quot;</strong> → <strong>&quot;Run anyway&quot;</strong>. The installer is plain text — you can open it in Notepad first to see exactly what it does.
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
              <strong>macOS note:</strong> the first time you double-click the <code>.command</code> file, macOS may say &quot;cannot be opened because it is from an unidentified developer&quot;. Right-click → <strong>Open</strong> → <strong>Open</strong> in the confirm dialog. Only needed once.
            </div>

            {/* Manual install — only for users who can't run the installers. */}
            <details className="text-xs mt-2">
              <summary className="cursor-pointer font-semibold text-stone-700">📖 Manual install (only if the installer didn&apos;t work)</summary>
              <div className="mt-3 space-y-3 text-stone-700">
                <a href="/downloads/smarto_print_bridge.py"
                   download="smarto_print_bridge.py"
                   className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 text-xs font-semibold"
                   data-testid="bridge-download">
                  <Download size={14} className="text-emerald-600" /> Download smarto_print_bridge.py (raw script)
                </a>
                <div className="p-3 rounded bg-white border border-stone-200">
                  <div className="font-semibold text-stone-900 mb-1">macOS (Python pre-installed)</div>
                  <ol className="list-decimal ml-5 space-y-0.5">
                    <li>Open <strong>Terminal</strong> (Cmd + Space → &quot;Terminal&quot;).</li>
                    <li><code className="bg-stone-100 px-1 rounded">cd ~/Downloads &amp;&amp; pip3 install --user requests zeroconf &amp;&amp; python3 smarto_print_bridge.py</code></li>
                    <li>Leave the Terminal window open. You should see &quot;Listening on http://0.0.0.0:9101&quot;.</li>
                  </ol>
                </div>
                <div className="p-3 rounded bg-white border border-stone-200">
                  <div className="font-semibold text-stone-900 mb-1">Windows</div>
                  <ol className="list-decimal ml-5 space-y-0.5">
                    <li>Install Python 3.8+ from <a className="text-indigo-700 hover:underline" href="https://www.python.org/downloads/" target="_blank" rel="noreferrer">python.org/downloads</a>. <strong>Tick &quot;Add to PATH&quot;</strong> during install.</li>
                    <li>Open <strong>PowerShell</strong> and run: <code className="bg-stone-100 px-1 rounded">pip install requests zeroconf</code></li>
                    <li>Right-click <code className="bg-stone-100 px-1 rounded">smarto_print_bridge.py</code> → Open with → Python.</li>
                  </ol>
                </div>
                <div className="p-3 rounded bg-indigo-50 border border-indigo-200">
                  <div className="font-semibold text-indigo-900 mb-1">After the Bridge is running</div>
                  <p>Open Smarto POS → Printer Settings → tick the green &quot;Use Print Bridge&quot; option (it auto-detects). Print a test receipt. Done.</p>
                </div>
              </div>
            </details>
          </CardContent>
        </Card>

        {/* iter630 — Auto-discovered printers panel (talks to local Bridge /discover). */}
        <Card className="border-sky-300 bg-sky-50/40" data-testid="auto-discover-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Network size={18} className="text-sky-700" />
              <h2 className="text-lg font-bold font-outfit">Step 5 — Auto-discover printers on the LAN</h2>
              <Badge className="bg-sky-600 text-white text-[10px]">NEW</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Once the Print Bridge is running on a Mac/PC, this button scans the local network for
              candidate ESC/POS printers (port 9100) and lists them so you can copy the IP straight
              into Smarto. Open this page <strong>on the same PC where the Bridge is running</strong> —
              the browser talks to <code className="bg-stone-100 px-1 rounded">{discovered.bridge_url}</code>.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={triggerDiscovery} disabled={discovered.status === 'scanning'}
                      className="bg-sky-600 hover:bg-sky-700 text-white"
                      data-testid="auto-discover-btn">
                {discovered.status === 'scanning' ? 'Scanning LAN…' : 'Scan now'}
              </Button>
              {discovered.status === 'error' && (
                <div className="text-[11px] text-rose-700 flex items-center gap-2 flex-wrap" data-testid="bridge-not-running-banner">
                  <span>
                    Couldn&apos;t reach the Bridge on {discovered.bridge_url}. The Bridge isn&apos;t running on this PC yet.
                  </span>
                  <a
                    href="/downloads/smarto_print_bridge_installer.bat"
                    download="SmartoPrintBridge_Installer.bat"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700"
                    data-testid="bridge-installer-windows-inline"
                  >
                    <Download size={11} /> Install (Windows)
                  </a>
                  <a
                    href="/downloads/smarto_print_bridge_installer.command"
                    download="SmartoPrintBridge_Installer.command"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700"
                    data-testid="bridge-installer-mac-inline"
                  >
                    <Apple size={11} /> Install (Mac)
                  </a>
                </div>
              )}
              {discovered.status === 'done' && (
                <span className="text-[11px] text-sky-800">
                  Found {discovered.printers.length} candidate(s)
                </span>
              )}
            </div>
            {discovered.printers.length > 0 && (
              <div className="space-y-1" data-testid="discovered-list">
                {discovered.printers.map((p, i) => (
                  <div key={`${p.ip}-${i}`}
                       className="flex items-center justify-between gap-2 p-2 rounded bg-white border border-sky-200 text-[12px]"
                       data-testid={`discovered-printer-${i}`}>
                    <div className="min-w-0 truncate">
                      <code className="font-mono">{p.ip}:{p.port}</code>
                      {p.name && <span className="ml-2 text-sky-700">{p.name}</span>}
                      {p.source && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">{p.source}</span>}
                    </div>
                    <Button size="sm" variant="outline"
                            onClick={() => { navigator.clipboard?.writeText(p.ip); }}
                            className="h-6 text-[10px]"
                            data-testid={`copy-ip-${i}`}>
                      Copy IP
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-stone-600 border-t border-sky-200 pt-2 mt-2">
              <strong>Tip:</strong> install <code className="bg-stone-100 px-1 rounded">zeroconf</code> on the Bridge machine (<code className="bg-stone-100 px-1 rounded">pip install zeroconf</code>) to also discover AirPrint / Bonjour printers automatically. Without it, only raw ESC/POS port 9100 printers show up.
              <br/><strong>Auto-update:</strong> the Bridge checks for newer versions on boot and prints the download URL. Set <code className="bg-stone-100 px-1 rounded">BRIDGE_AUTO_UPDATE=1</code> to also self-replace automatically. Hit <code className="bg-stone-100 px-1 rounded">http://localhost:9101/version</code> any time to check.
            </div>

            {/* iter632 — Push test print */}
            <div className="border-t border-sky-200 pt-3 mt-2" data-testid="push-test-print-section">
              <div className="text-[11px] font-bold text-sky-900 mb-1">⚡ Push a test print</div>
              <div className="text-[11px] text-stone-600 mb-2">
                Sends a small receipt directly through the Bridge to confirm the iPad → cloud → Bridge → printer path works.
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  placeholder="Printer IP (e.g., 192.168.1.223)"
                  value={testIp}
                  onChange={(e) => setTestIp(e.target.value)}
                  className="flex-1 min-w-[180px] h-8 text-[12px] px-2 rounded border border-stone-300"
                  data-testid="push-test-ip-input"
                />
                <Button onClick={pushTestPrint} disabled={testing}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-[11px]"
                        data-testid="push-test-print-btn">
                  {testing ? 'Sending…' : '🖨️ Push test print'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </>)}

        {appleHelp && (<>
        {/* iter647 — Big iPad guidance card. The user kept getting confused;
            this lays out the full path in three steps with concrete
            "what to type on the PC" snippets and an unmistakable "you
            cannot install Python on iPad" warning. */}
        <Card className="border-2 border-orange-300 bg-orange-50/60 dark:bg-orange-950/20" data-testid="ipad-guide-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Apple size={20} className="text-orange-700" />
              <h2 className="text-lg font-bold font-outfit text-orange-900 dark:text-orange-200">Printing from iPad — read this first</h2>
            </div>
            <div className="bg-rose-100 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-900 rounded-lg p-3 text-xs space-y-1">
              <p className="font-semibold text-rose-900 dark:text-rose-200">
                ⚠️ Python <u>cannot</u> be installed on iPad / iOS.
              </p>
              <p className="text-rose-800 dark:text-rose-300">
                That&apos;s an Apple restriction, not Smarto&apos;s. The Print Bridge lives
                on a <strong>Mac, Windows PC, or any cheap mini-PC</strong> — never on the iPad itself.
                The iPad&apos;s job is just to send the receipt to Smarto cloud → which forwards it to that PC&apos;s Bridge → which talks to your printer.
              </p>
            </div>
            <div className="text-xs space-y-2">
              <p className="font-semibold">Three things you need at every branch:</p>
              <ol className="list-decimal list-inside space-y-1.5 ml-1">
                <li>📱 Any number of <strong>iPads</strong> (your cashiers) — already have these.</li>
                <li>💻 <strong>One</strong> PC/Mac/mini-PC on the same Wi-Fi — runs the Bridge.  Old Windows laptop, even a $150 mini-PC works. <em>It just needs to stay on while you&apos;re open.</em></li>
                <li>🖨️ A thermal printer on the same Wi-Fi (Epson TM-T20II/III, Star TSP143, etc.).</li>
              </ol>
              <p className="font-semibold mt-2">Don&apos;t have a PC? Three alternatives:</p>
              <ul className="list-disc list-inside space-y-1 ml-1 text-muted-foreground">
                <li>Buy a <strong>Lantronix xPrintServer</strong> (~$120). Makes your LAN printer visible to iPad as AirPrint — no Bridge needed at all.</li>
                <li>Replace your printer with an <strong>Epson TM-m30II</strong> — AirPrint is built in.</li>
                <li>Get a <strong>Raspberry Pi 4</strong> (~$50) — runs Python perfectly, plug it in once and forget about it.</li>
              </ul>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold hover:text-orange-700" data-testid="ipad-guide-expand">
                ▸ Step-by-step setup with the exact commands to type
              </summary>
              <div className="mt-2 space-y-2 pl-2 border-l-2 border-orange-300">
                <p><strong>1. Find a Mac/PC at the branch.</strong> Cashier desktop, manager laptop, anything.</p>
                <p><strong>2. Install Python 3 on that PC</strong> (one-time, 3 min).</p>
                <ul className="list-disc list-inside text-muted-foreground ml-2">
                  <li>Windows: <a href="https://www.python.org/downloads/windows/" target="_blank" rel="noreferrer" className="text-orange-700 underline">python.org/downloads/windows</a> — tick <em>&quot;Add Python to PATH&quot;</em> during install.</li>
                  <li>macOS: <code className="bg-stone-100 px-1 rounded">brew install python</code> or download from python.org.</li>
                </ul>
                <p><strong>3. Open this page on that same PC.</strong> Download <code className="bg-stone-100 px-1 rounded">smarto_print_bridge.py</code> from Step 4 above.</p>
                <p><strong>4. Get your relay token</strong> from the sky-blue card below (Step 5b). Copy it.</p>
                <p><strong>5. Open Terminal (macOS) or Command Prompt (Windows):</strong></p>
                <pre className="bg-stone-900 text-amber-300 p-2.5 rounded text-[10px] overflow-x-auto">macOS / Linux:
  export BRIDGE_RELAY_TOKEN=&apos;PASTE_THE_TOKEN&apos;
  cd Downloads
  python3 smarto_print_bridge.py

Windows (Command Prompt):
  set BRIDGE_RELAY_TOKEN=PASTE_THE_TOKEN
  cd Downloads
  python smarto_print_bridge.py</pre>
                <p><strong>6. Wait 5 sec.</strong> You should see:</p>
                <pre className="bg-stone-900 text-emerald-400 p-2.5 rounded text-[10px] overflow-x-auto">  Cloud relay: ON (https://smartoerp.com)
[bridge][relay] registered as &lt;some-uuid&gt;</pre>
                <p><strong>7. On your iPad, refresh smartoerp.com/printer-setup.</strong> The Bridge appears with a green dot under <em>Registered Bridges</em>. Print a test — the receipt comes out.</p>
                <p className="text-emerald-700 font-semibold">That&apos;s it. The PC must stay on. The iPad doesn&apos;t need anything special.</p>
              </div>
            </details>
            <div className="flex items-center gap-2 pt-2 border-t border-orange-200">
              <Button size="sm" variant="outline" onClick={() => nav('/print-history')} data-testid="ipad-view-history">
                <Printer size={13} className="mr-1.5" /> View Print History
              </Button>
              <span className="text-[11px] text-muted-foreground">See every receipt your iPads send (with re-print on failures).</span>
            </div>
          </CardContent>
        </Card>
        </>)}

        {showAdvanced && (<>
        {/* iter646 — Cloud Relay (iPad printing). Lets iPads send print
            jobs to a Bridge running on a Mac/PC at the restaurant. */}
        <Card className="border-sky-300 bg-sky-50/50" data-testid="cloud-relay-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Cloud size={18} className="text-sky-700" />
              <h2 className="text-lg font-bold font-outfit">Step 5b — Cloud Relay (iPad printing)</h2>
              <Badge className="bg-sky-600 text-white text-[10px]">NEW</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              iPads can&apos;t reach <code className="bg-stone-100 px-1 rounded">localhost:9101</code> on themselves
              (iOS doesn&apos;t run Python). The Cloud Relay solves this: paste this token into the Bridge
              running on your Mac/PC. Smarto will then forward iPad print jobs through the cloud → the Bridge → the printer.
            </p>
            <div className="bg-white dark:bg-stone-900 border rounded-lg p-3 space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Your relay token</div>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="flex-1 min-w-[260px] font-mono text-xs px-2.5 py-2 bg-stone-50 dark:bg-stone-800 rounded border break-all" data-testid="relay-token-value">
                  {relayLoading ? 'Loading…' : (relayToken || '—')}
                </code>
                <Button size="sm" variant="outline" onClick={copyToken} disabled={!relayToken} data-testid="relay-token-copy">
                  <Copy size={13} className="mr-1" /> Copy
                </Button>
                <Button size="sm" variant="ghost" onClick={rotateRelay} disabled={!relayToken} className="text-rose-600" data-testid="relay-token-rotate">
                  <RefreshCw size={13} className="mr-1" /> Rotate
                </Button>
              </div>
              <details className="text-[11px] text-muted-foreground" data-testid="relay-token-howto">
                <summary className="cursor-pointer hover:text-sky-700 font-semibold">▸ How to enable on the Bridge (1 minute)</summary>
                <ol className="list-decimal list-inside space-y-1 mt-2 pl-2">
                  <li>Re-download the Bridge — it must be <strong>v1.3 or newer</strong> (it has the cloud-relay code).</li>
                  <li>On the Mac/PC running the Bridge, stop the current Bridge (Ctrl-C).</li>
                  <li>Set the token as an env var and restart, e.g.:
                    <pre className="bg-stone-900 text-amber-300 p-2 rounded mt-1 overflow-x-auto text-[10px]">macOS / Linux:&#10;  export BRIDGE_RELAY_TOKEN=&apos;{relayToken || 'PASTE_TOKEN'}&apos;&#10;  python3 smarto_print_bridge.py&#10;&#10;Windows (cmd):&#10;  set BRIDGE_RELAY_TOKEN={relayToken || 'PASTE_TOKEN'}&#10;  python smarto_print_bridge.py</pre>
                  </li>
                  <li>The Bridge prints <code className="bg-stone-100 px-1 rounded">Cloud relay: ON</code> + <code className="bg-stone-100 px-1 rounded">[bridge][relay] registered as &lt;id&gt;</code> on startup.</li>
                  <li>The card below will show your Bridge with a green dot within 30 sec.</li>
                </ol>
              </details>
            </div>
            <div data-testid="bridges-list">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Registered bridges</div>
              {bridges.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">No Bridge has registered with the cloud yet. Follow the steps above.</div>
              ) : (
                <ul className="space-y-1.5">
                  {bridges.map(b => (
                    <li key={b.id} className="flex items-center justify-between bg-white dark:bg-stone-900 border rounded-lg px-3 py-2" data-testid={`bridge-row-${b.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${b.online ? 'bg-emerald-500' : 'bg-stone-400'}`}></span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{b.name || 'Print Bridge'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">v{b.version || '?'} · {b.id.slice(0, 8)} · last seen {b.last_seen?.split('T')[1]?.slice(0, 8) || '—'}</div>
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.online ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}>
                        {b.online ? 'Online' : 'Offline'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        {/* iter645 — Bridge Diagnostic. Live probe of /health /version /ping so
            users can self-diagnose without guessing why the banner is yellow. */}
        <Card className="border-amber-300 bg-amber-50/40" data-testid="bridge-diagnostic-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Wrench size={18} className="text-amber-700" />
              <h2 className="text-lg font-bold font-outfit">Step 6 — Bridge Diagnostic</h2>
              <Badge className="bg-amber-600 text-white text-[10px]">NEW</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Click to live-probe <code className="bg-stone-100 px-1 rounded">http://localhost:9101</code>.
              Run this <strong>on the same PC/Mac where the Bridge is running</strong> — it can&apos;t reach a
              Bridge across the LAN, only the one on the same machine as the browser.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={runDiagnostic} disabled={diagRunning}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="bridge-diag-btn">
                {diagRunning ? 'Probing…' : '🔬 Run diagnostic'}
              </Button>
              {diag && (
                <span className={`text-[11px] font-semibold ${diag.overall ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {diag.overall ? '✓ All probes passed' : '✗ Bridge unreachable or misconfigured'} — {diag.ts}
                </span>
              )}
            </div>
            {diag && (
              <div className="space-y-1.5 mt-2" data-testid="bridge-diag-results">
                {diag.checks.map((c, i) => (
                  <div key={i}
                       className={`p-2 rounded border text-[11px] ${c.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                       data-testid={`bridge-diag-result-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{c.ok ? '✓' : '✗'} {c.name}</span>
                      <span className="text-stone-500">{c.status || '—'} · {c.ms}ms</span>
                    </div>
                    {c.error && <div className="text-rose-700 mt-1 font-mono">{c.error}</div>}
                    {c.body && <div className="text-stone-600 mt-1 font-mono truncate">{c.body}</div>}
                  </div>
                ))}
                {!diag.overall && (
                  <div className="p-2 rounded bg-rose-100 border border-rose-300 text-[11px] text-rose-900 mt-2">
                    <strong>Common causes:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                      <li>Bridge is not running — open Terminal/CMD and run <code className="bg-white px-1 rounded">python3 smarto_print_bridge.py</code> on this same PC.</li>
                      <li>You&apos;re on an iPad — <strong>Python doesn&apos;t run on iOS</strong>. The Bridge lives on a Mac/PC; iPads send print jobs through Smarto&apos;s cloud → that PC&apos;s Bridge.</li>
                      <li>Firewall blocked port 9101 — allow loopback in Windows Defender / macOS Firewall.</li>
                      <li>An AV / corporate proxy intercepting localhost — temporarily disable to test.</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter732 — Printer Memory Reset card. Lives just above the
            Print Log so the operator's eye flows naturally: "log shows
            OK but no paper → reset → diagnostic → check log again". */}
        <Card className="border-amber-300 bg-amber-50/50" data-testid="printer-reset-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <RotateCcw size={18} className="text-amber-700" />
              <h2 className="text-lg font-bold font-outfit">Step 6.5 — Reset Printer Memory</h2>
              <Badge className="bg-amber-600 text-white text-[10px]">NEW</Badge>
            </div>
            <p className="text-xs text-amber-900/85 leading-relaxed">
              Print Log says <strong className="text-emerald-700">OK</strong> but no paper coming out?
              The printer is probably stuck in a recoverable firmware halt (ASB error — cover-open false-positive,
              jammed buffer, leftover bytes from a bad XML). Tap <strong>Clear Printer Memory</strong> and we&apos;ll
              send the Epson <code className="text-[10px] bg-white px-1 py-0.5 rounded border">&lt;recovery/&gt;</code> command
              to flush the printer&apos;s hardware buffer, clear our in-app de-duplication cache, AND send a tiny
              SMARTO diagnostic slip so you can SEE if the hardware is alive.
            </p>

            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-amber-900 mb-1">Printer IP</label>
                <input
                  type="text"
                  value={resetIp}
                  onChange={(e) => setResetIp(e.target.value)}
                  placeholder="192.168.1.150"
                  className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
                  data-testid="reset-printer-ip-input"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-amber-900 mb-1">Device ID</label>
                <input
                  type="text"
                  value={resetDevId}
                  onChange={(e) => setResetDevId(e.target.value)}
                  placeholder="local_printer"
                  className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
                  data-testid="reset-printer-devid-input"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-amber-900 mb-1">Paper</label>
                <select
                  value={resetPaper}
                  onChange={(e) => setResetPaper(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
                  data-testid="reset-printer-paper-select"
                >
                  <option value="80mm">80 mm</option>
                  <option value="58mm">58 mm</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button
                size="sm"
                onClick={runFullReset}
                disabled={resetBusy || !resetIp.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="reset-printer-full-btn"
              >
                <RotateCcw size={14} className={`mr-1 ${resetBusy ? 'animate-spin' : ''}`} />
                {resetBusy ? 'Working…' : 'Clear Printer Memory + Test'}
              </Button>
              <Button
                size="sm"
                onClick={runDiagnosticOnly}
                disabled={resetBusy || !resetIp.trim()}
                variant="outline"
                className="border-amber-400 text-amber-800 hover:bg-amber-100"
                data-testid="reset-printer-diagnostic-btn"
              >
                <Zap size={14} className="mr-1" /> Print diagnostic slip only
              </Button>
              <Button
                size="sm"
                onClick={runImageTest}
                disabled={resetBusy || !resetIp.trim()}
                variant="outline"
                className="border-sky-400 text-sky-800 hover:bg-sky-100"
                data-testid="reset-printer-image-test-btn"
              >
                <Zap size={14} className="mr-1" /> Print IMAGE test (raster)
              </Button>
              <Button
                size="sm"
                onClick={flushDebounceOnly}
                variant="ghost"
                className="text-amber-800 hover:bg-amber-100"
                data-testid="reset-printer-debounce-btn"
              >
                <Trash2 size={14} className="mr-1" /> Clear in-app cache only
              </Button>
            </div>

            <div className="p-2.5 rounded bg-white border border-amber-200 text-[11px] text-amber-900/85 leading-relaxed" data-testid="reset-printer-hint">
              <strong>How to read the result:</strong>
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                <li>If a tiny <strong>SMARTO</strong> slip prints → hardware is healthy. The receipt XML
                  (logo / QR / large totals) is the culprit; try toggling off the logo or QR in Receipt Settings.</li>
                <li>If <strong>nothing</strong> prints but the Print Log shows <strong className="text-emerald-700">OK</strong> →
                  iOS Safari has not accepted the printer&apos;s self-signed cert. Tap <strong>Configure printer IP in POS</strong> above,
                  then visit <code className="text-[10px] bg-amber-50 px-1 rounded">https://{resetIp.trim() || '<printer-ip>'}/</code> once
                  in Safari → &quot;Show Details&quot; → &quot;Visit this website&quot;.</li>
                <li>If the Print Log shows an error → copy the log and share with support.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* iter722 — Print Log. Every ePOS print attempt is logged to
            localStorage so users can share a structured trace with support
            when receipts don't come out (no more "what error?" guesswork). */}
        <Card className="border-sky-300 bg-sky-50/40" data-testid="print-log-section">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <FileText size={18} className="text-sky-700" />
              <h2 className="text-lg font-bold font-outfit">Step 7 — Print Log</h2>
              <Badge className="bg-sky-600 text-white text-[10px]">NEW</Badge>
              <Badge variant="outline" className="text-[10px] bg-white">
                {logRows.length} row{logRows.length === 1 ? '' : 's'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Every time the POS sends a job to a printer it lands here with the
              <strong> timestamp, printer IP, device ID, order #, total, and any error</strong>.
              When a receipt fails to print, tap <strong>Copy</strong> or
              <strong> Download</strong> and share the log — support can read
              the exact failure mode instead of guessing.
            </p>

            {/* iter745 — Print receipt as IMAGE (raster) toggle */}
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border ${rasterMode ? 'border-emerald-400 bg-emerald-50' : 'border-stone-300 bg-white'}`}
              data-testid="raster-mode-toggle-row"
            >
              <button
                type="button"
                role="switch"
                aria-checked={rasterMode}
                onClick={toggleRasterMode}
                data-testid="raster-mode-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${rasterMode ? 'bg-emerald-600' : 'bg-stone-300'}`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${rasterMode ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">Print receipt as image (Arabic-safe)</span>
                  <Badge variant="outline" className={`text-[10px] ${rasterMode ? 'border-emerald-500 text-emerald-700' : 'border-stone-300 text-stone-500'}`}>
                    {rasterMode ? 'IMAGE MODE: ON' : 'TEXT MODE'}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  <strong>ON (recommended for Arabic):</strong> the whole receipt (logo + Arabic + English + items + totals) is rendered as one bitmap and sent to the printer — prints perfectly on any thermal printer regardless of its built-in fonts. The ZATCA QR is still added natively at the bottom.<br/>
                  <strong>OFF (text mode):</strong> receipt prints as plain text — faster &amp; sharper, but Arabic only prints if your printer has an Arabic font. Use this if the image mode doesn&apos;t print on your hardware. Company &amp; branch name still appear bilingually at the top.
                </p>
              </div>
            </div>

            {/* iter753 — Print size (receipt scale) */}
            <div
              className="flex items-start gap-3 p-3 rounded-lg border border-stone-300 bg-white"
              data-testid="print-size-row"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">Print size</span>
                  <Badge variant="outline" className="text-[10px] border-sky-500 text-sky-700">
                    {printScale === 'large' ? 'LARGE' : 'NORMAL'}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  <strong>Normal:</strong> standard density (more text per line).<br/>
                  <strong>Large:</strong> bigger, easier-to-read characters (fewer columns per line). Applies to image-mode receipts.
                </p>
                <div className="inline-flex mt-2 rounded-lg border border-stone-300 overflow-hidden" role="group" data-testid="print-size-toggle">
                  <button
                    type="button"
                    onClick={() => changePrintScale('normal')}
                    data-testid="print-size-normal"
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${printScale === 'normal' ? 'bg-sky-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    onClick={() => changePrintScale('large')}
                    data-testid="print-size-large"
                    className={`px-4 py-1.5 text-sm font-medium transition-colors border-l border-stone-300 ${printScale === 'large' ? 'bg-sky-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                  >
                    Large
                  </button>
                </div>
              </div>
            </div>

            {/* iter741 — Developer toggle: Capture XML to Print Log */}
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border ${captureXml ? 'border-fuchsia-400 bg-fuchsia-50' : 'border-stone-300 bg-white'}`}
              data-testid="capture-xml-toggle-row"
            >
              <button
                type="button"
                role="switch"
                aria-checked={captureXml}
                onClick={toggleCaptureXml}
                data-testid="capture-xml-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500 ${captureXml ? 'bg-fuchsia-600' : 'bg-stone-300'}`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${captureXml ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">Capture XML to Print Log</span>
                  <Badge variant="outline" className={`text-[10px] ${captureXml ? 'border-fuchsia-500 text-fuchsia-700' : 'border-stone-300 text-stone-500'}`}>
                    {captureXml ? 'DEVELOPER MODE: ON' : 'OFF'}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  When ON, every print writes its <strong>raw Epson ePOS XML payload</strong> into the print log so you can inspect EXACTLY what the firmware received. Use this to hunt &quot;log says OK but no paper came out&quot; bugs without Safari Web Inspector. Off by default — turn ON, print a receipt, then expand the row below to copy the XML and share with support.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={refreshLog}
                variant="outline"
                className="border-sky-400 text-sky-700 hover:bg-sky-100"
                data-testid="print-log-refresh-btn"
              >
                <RefreshCw size={14} className="mr-1" /> Refresh
              </Button>
              <Button
                size="sm"
                onClick={copyLog}
                disabled={!logRows.length}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="print-log-copy-btn"
              >
                <Copy size={14} className="mr-1" /> Copy
              </Button>
              <Button
                size="sm"
                onClick={downloadLog}
                disabled={!logRows.length}
                variant="outline"
                className="border-sky-400 text-sky-700 hover:bg-sky-100"
                data-testid="print-log-download-btn"
              >
                <Download size={14} className="mr-1" /> Download .txt
              </Button>
              <Button
                size="sm"
                onClick={() => setLogExpanded(v => !v)}
                disabled={!logRows.length}
                variant="ghost"
                data-testid="print-log-toggle-btn"
              >
                {logExpanded ? 'Hide details' : 'Show details'}
              </Button>
              <Button
                size="sm"
                onClick={wipeLog}
                disabled={!logRows.length}
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 ml-auto"
                data-testid="print-log-clear-btn"
              >
                <Trash2 size={14} className="mr-1" /> Clear
              </Button>
            </div>

            {logRows.length === 0 ? (
              <div className="p-3 rounded bg-white border border-dashed border-sky-300 text-[11px] text-stone-500 text-center" data-testid="print-log-empty">
                No print attempts recorded yet. Print a receipt and reload this page — every job will show up here.
              </div>
            ) : logExpanded ? (
              <div className="space-y-1.5 max-h-[360px] overflow-y-auto" data-testid="print-log-rows">
                {[...logRows].reverse().map((r, i) => (
                  <div
                    key={i}
                    className={`p-2 rounded border text-[11px] font-mono ${r.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                    data-testid={`print-log-row-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-semibold">
                        {r.ok ? '✓' : '✗'} {new Date(r.ts).toLocaleString()}
                      </span>
                      <span className="text-stone-500">
                        [{r.channel}] {r.duration_ms != null ? `${r.duration_ms}ms` : ''}
                      </span>
                    </div>
                    <div className="text-stone-700 mt-0.5 truncate">
                      printer: {r.printer?.name || '-'} · {r.printer?.ip || '-'} · devid=&quot;{r.printer?.deviceId || '-'}&quot; · {r.printer?.paper_width || '-'}
                    </div>
                    <div className="text-stone-700 truncate">
                      order#={r.receipt?.order_number || '-'} · total={r.receipt?.total ?? '-'} · items={r.receipt?.items_count ?? '-'}
                    </div>
                    {/* iter727 — Show logo + QR render outcome per row
                        so users can spot "printer worked but logo got
                        skipped" without exporting the log. */}
                    {(r.result?.logo || r.result?.qr) && (
                      <div className="text-stone-600 text-[10px]">
                        logo=<span className={r.result?.logo?.startsWith('rendered') ? 'text-emerald-700' : r.result?.logo === 'no-url' ? 'text-stone-500' : 'text-amber-700'}>{r.result?.logo || '-'}</span>
                        {' · '}qr=<span className={r.result?.qr === 'emitted' ? 'text-emerald-700' : 'text-amber-700'}>{r.result?.qr || '-'}</span>
                      </div>
                    )}
                    {r.result?.eposResult && (
                      <div className={`text-[10px] break-words ${r.result.eposResult.includes('success=false') ? 'text-rose-700 font-semibold' : r.result.eposResult.includes('success=true') ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="log-epos-result">
                        printer reply: {r.result.eposResult}
                      </div>
                    )}
                    {r.ok ? (
                      <div className="text-emerald-700 truncate">mode={r.result?.mode || '-'}{r.result?.msg ? ` · ${r.result.msg}` : ''}</div>
                    ) : (
                      <div className="text-rose-700 break-words">
                        error={r.result?.error || '-'} · msg={r.result?.msg || '-'}
                      </div>
                    )}
                    {/* iter741 — Raw XML inspector for the row (only
                        present when the developer toggle was ON at the
                        time the row was logged). */}
                    {r.result?.xml && (
                      <div className="mt-1.5 border-t border-stone-200 pt-1.5" data-testid={`print-log-xml-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[9px] border-fuchsia-500 text-fuchsia-700">
                            XML CAPTURED · {r.result.xml.length} chars
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setXmlExpandedRow(xmlExpandedRow === i ? null : i)}
                            className="h-6 text-[10px] text-fuchsia-700 hover:bg-fuchsia-100"
                            data-testid={`print-log-xml-toggle-${i}`}
                          >
                            {xmlExpandedRow === i ? 'Hide raw XML' : 'Show raw XML'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyXmlRow(r.result.xml)}
                            className="h-6 text-[10px] text-fuchsia-700 hover:bg-fuchsia-100"
                            data-testid={`print-log-xml-copy-${i}`}
                          >
                            <Copy size={11} className="mr-1" /> Copy XML
                          </Button>
                        </div>
                        {xmlExpandedRow === i && (
                          <pre
                            className="mt-1.5 p-2 rounded bg-stone-900 text-fuchsia-200 text-[9.5px] leading-tight max-h-[260px] overflow-auto whitespace-pre-wrap break-all"
                            data-testid={`print-log-xml-pre-${i}`}
                          >{r.result.xml}</pre>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-2 rounded bg-white border border-sky-200 text-[11px] text-stone-600" data-testid="print-log-summary">
                Latest {Math.min(logRows.length, 3)} row{logRows.length === 1 ? '' : 's'}:&nbsp;
                {[...logRows].slice(-3).reverse().map((r, i) => (
                  <span key={i} className={`mr-3 ${r.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {r.ok ? '✓' : '✗'} {new Date(r.ts).toLocaleTimeString()} · {r.printer?.ip || '-'}{!r.ok && r.result?.error ? ` (${r.result.error})` : ''}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>)}
      </div>
    </DashboardLayout>
  );
}
